import type { TOC } from '@ember/component/template-only';
import { array } from '@ember/helper';
import { PrismLoadingBar, PrismViewSwitcher } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import { and, gt, not, or } from 'ember-truth-helpers';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralDropdownsDropdownPageSize from 'lumen/components/general/dropdowns/dropdown-page-size';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralTablesPaginationFooter from 'lumen/components/general/tables/pagination-footer';
import MarketSnapshotsCard from 'lumen/components/market/snapshots/card';
import MarketSnapshotsDataPreferences from 'lumen/components/market/snapshots/data-preferences';
import MarketSnapshotsKpiSection from 'lumen/components/market/snapshots/kpi-section';
import MarketSnapshotsTable from 'lumen/components/market/snapshots/table';
import MarketSnapshotsTableLayout from 'lumen/components/market/snapshots/table-layout';
import type MarketSnapshotsOverviewController from 'lumen/controllers/market/snapshots/overview';
import contextualPageTitle from 'lumen/helpers/contextual-page-title';
import type MarketSnapshotsOverviewRoute from 'lumen/routes/market/snapshots/overview';
import {
  FIRST_HEADER_HEIGHT,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import {
  GROUP_BY,
  OVERVIEW_KPI_NAMES,
} from 'lumen/utils/constants/market-snapshots-constants';
import type { ModelFrom } from 'lumen/utils/routes';

interface MarketSnapshotsOverviewSignature {
  Args: {
    controller: MarketSnapshotsOverviewController;
    model: ModelFrom<MarketSnapshotsOverviewRoute>;
  };
}

const LOADING_BAR_POSITION_TOP = FIRST_HEADER_HEIGHT + SUB_HEADER_HEIGHT;

<template>
  {{pageTitle (contextualPageTitle (t "market.snapshots.view.overview"))}}

  <GeneralPageHeader @isSmall={{true}}>
    <div class="content-wrapper flex items-center">
      {{! @glint-expect-error: Incorrect type }}
      <GeneralDatepickersDatepickerWithSidebar
        @maxDate={{@controller.maxDate}}
        @maxRange="2 years"
        @range={{@controller.dateRange}}
        @rangeChanged={{@controller.updateDateRange}}
        @sidebarLists={{@controller.datepickerSidebarLists}}
        @triggerLabelFormatter={{if
          @controller.isMonthView
          @controller.monthTriggerLabelFormatter
        }}
      />

      <div class="ms-auto">
        <PrismViewSwitcher
          @activeButtonId={{@controller.groupBy}}
          @onSetActiveButtonId={{@controller.setGroupBy}}
          as |ViewSwitcher|
        >
          <ViewSwitcher.button
            @id={{GROUP_BY.PROPERTY}}
            @label={{t "market.snapshots.group_by.properties"}}
            data-test-id="group-by-property"
          />

          <ViewSwitcher.button
            @id={{GROUP_BY.MONTH}}
            @label={{t "market.snapshots.group_by.months"}}
            data-test-id="group-by-month"
          />
        </PrismViewSwitcher>
      </div>
    </div>
  </GeneralPageHeader>

  {{#if @controller.model.loadDataItemsTask.last.isRunning}}
    <PrismLoadingBar
      @isFixed={{true}}
      @positionTop={{LOADING_BAR_POSITION_TOP}}
    />
  {{/if}}

  <GeneralPageMain>
    <div class="content-wrapper main-content flex h-full flex-col gap-600">
      <div class="flex items-center gap-200">
        <h2 class="text-neutral-emphasis">{{t
            "market.snapshots.view.overview"
          }}</h2>

        {{#if (and @controller.totalRecords (not @controller.isMonthView))}}
          <span class="text-300--regular">
            {{t "general.hotel.count" count=@controller.totalRecords}}
          </span>
        {{/if}}

        <MarketSnapshotsDataPreferences
          @dataPreferences={{@controller.dataPreferences}}
          @onSave={{@controller.updateDataPreferences}}
          class="ms-auto"
        />
      </div>

      <MarketSnapshotsKpiSection
        @comparisonMetricType={{@controller.dataPreferences.sdlyColumn}}
        @currency={{@controller.currency}}
        @isLoading={{@controller.areKpisLoading}}
        @kpiNames={{OVERVIEW_KPI_NAMES}}
        @kpis={{@controller.kpis}}
        @varianceType={{@controller.dataPreferences.variance}}
      />

      <div class="flex items-center justify-between">
        <h3 class="text-neutral-emphasis">{{t "general.breakdown"}}</h3>

        <MarketSnapshotsTableLayout
          @columnMetrics={{@controller.columnMetrics}}
          @columnSettings={{@controller.columnSettings}}
          @kpiColumnSettings={{@controller.kpiColumnSettings}}
          @onSave={{@controller.updateTableLayoutPreferences}}
          @tableLayoutPreferences={{@controller.tableLayoutPreferences}}
        />
      </div>

      <MarketSnapshotsCard
        @showFooter={{and
          (not @controller.isMonthView)
          (gt
            @controller.model.loadDataItemsTask.lastSuccessful.value.meta.count
            0
          )
        }}
      >
        <:content>
          <MarketSnapshotsTable
            @dataItems={{or
              @controller.model.loadDataItemsTask.last.value.items
              (array)
            }}
            @groupBy={{@controller.groupBy}}
            @groupColumnsBy={{@controller.tableLayoutPreferences.groupColumnsBy}}
            @isLoading={{@controller.model.loadDataItemsTask.last.isRunning}}
            @onSort={{@controller.onSort}}
            @orderBy={{@controller.orderBy}}
            @totals={{@controller.model.loadDataItemsTask.last.value.meta.totals}}
            @varianceType={{@controller.dataPreferences.variance}}
            @visibleKpis={{@controller.visibleKpis}}
            @visibleMetricTypes={{@controller.visibleMetricTypes}}
          />
        </:content>

        <:footer>
          <GeneralTablesPaginationFooter
            @currentPage={{@controller.page}}
            @lastPage={{or
              @controller.model.loadDataItemsTask.lastSuccessful.value.meta.page_count
              1
            }}
            @onChangePageSize={{@controller.onChangePageSize}}
            @onSetPage={{@controller.setPage}}
            @pageSizeDropdown={{GeneralDropdownsDropdownPageSize}}
            @pageSizeOptions={{@controller.pageSizeOptions}}
            @pageSizeValue={{@controller.currentPageSizeOption}}
            @showingText={{@controller.showingText}}
          />
        </:footer>
      </MarketSnapshotsCard>
    </div>
  </GeneralPageMain>
</template> satisfies TOC<MarketSnapshotsOverviewSignature>;
